// React Query hooks for Content Versions (Inline AI Editor)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import type {
  ContentComponentType,
  ContentVersionsResponse,
  GenerateContentRequest,
  GenerateContentResponse,
  ActivateVersionResponse,
  DeleteVersionResponse,
  ContentVersion,
} from '../../services/unified/contentVersionsModule';

// ============================================================================
// Query Keys
// ============================================================================

export const contentVersionsKeys = {
  all: ['contentVersions'] as const,
  versions: (jobId: string, componentType: ContentComponentType) =>
    [...contentVersionsKeys.all, jobId, componentType] as const,
};

// ============================================================================
// Queries
// ============================================================================

/**
 * Get content versions for a job component
 */
export function useContentVersions(
  jobId: string,
  componentType: ContentComponentType,
  options?: { enabled?: boolean }
) {
  return useQuery<ContentVersionsResponse, Error>({
    queryKey: contentVersionsKeys.versions(jobId, componentType),
    queryFn: () => unifiedApiService.contentVersions.getVersions(jobId, componentType),
    enabled: options?.enabled !== false && !!jobId && !!componentType,
    staleTime: 30000, // 30 seconds
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Generate new content using AI
 */
export function useGenerateContent() {
  const queryClient = useQueryClient();

  return useMutation<
    GenerateContentResponse,
    Error,
    {
      jobId: string;
      componentType: ContentComponentType;
      request?: GenerateContentRequest;
    }
  >({
    mutationFn: ({ jobId, componentType, request }) =>
      unifiedApiService.contentVersions.generate(jobId, componentType, request),
    onSuccess: (_, { jobId, componentType }) => {
      // Invalidate versions query to refetch
      queryClient.invalidateQueries({
        queryKey: contentVersionsKeys.versions(jobId, componentType),
      });
      // Also invalidate the job query to reflect the new content
      queryClient.invalidateQueries({
        queryKey: ['jobs', 'detail', jobId],
      });
      // Invalidate all jobs lists to refresh the job cards
      queryClient.invalidateQueries({
        queryKey: ['jobs'],
      });
    },
  });
}

/**
 * Activate a specific version
 */
export function useActivateVersion() {
  const queryClient = useQueryClient();

  return useMutation<
    ActivateVersionResponse,
    Error,
    {
      jobId: string;
      componentType: ContentComponentType;
      versionId: string;
    }
  >({
    mutationFn: ({ jobId, componentType, versionId }) =>
      unifiedApiService.contentVersions.activate(jobId, componentType, versionId),
    onSuccess: (_, { jobId, componentType }) => {
      // Invalidate versions query
      queryClient.invalidateQueries({
        queryKey: contentVersionsKeys.versions(jobId, componentType),
      });
      // Invalidate job query to reflect the activated content
      queryClient.invalidateQueries({
        queryKey: ['jobs', 'detail', jobId],
      });
      // Invalidate all jobs lists to refresh the job cards
      queryClient.invalidateQueries({
        queryKey: ['jobs'],
      });
    },
  });
}

/**
 * Delete a version
 */
export function useDeleteVersion() {
  const queryClient = useQueryClient();

  return useMutation<
    DeleteVersionResponse,
    Error,
    {
      jobId: string;
      componentType: ContentComponentType;
      versionId: string;
    }
  >({
    mutationFn: ({ jobId, componentType, versionId }) =>
      unifiedApiService.contentVersions.deleteVersion(jobId, componentType, versionId),
    onSuccess: (_, { jobId, componentType }) => {
      // Invalidate versions query
      queryClient.invalidateQueries({
        queryKey: contentVersionsKeys.versions(jobId, componentType),
      });
    },
  });
}

// ============================================================================
// Re-export types for convenience
// ============================================================================

export type {
  ContentComponentType,
  ContentVersionsResponse,
  GenerateContentRequest,
  GenerateContentResponse,
  ActivateVersionResponse,
  DeleteVersionResponse,
  ContentVersion,
};
